/**
 * APPLICATIONS tab — the new Portal UI (docs/plans/2026-09-host-event-edit/handoff,
 * "Applications"). WAITING ON YOU: a white card of applicant blocks (56px round avatar,
 * NAME, @handle · meta, Vibe Check in Times italic, the application message, ACCEPT /
 * MESSAGE FIRST / DECLINE pills) or the centred NOTHING TO REVIEW. empty state; the
 * ALREADY ANSWERED card (REMOVE on accepted, REINVITE on declined — what ApplicationRow
 * did); the INTERESTED list for external-ticketing click-throughs; party-trick review.
 * Actions are absent (not disabled) outside selling/funding/greenlit.
 */
import React from 'react';
import { dateLabel, ago } from '../../utils/surfaceFormat';
import { committedCents } from './hostOpsModel';
import { Card, SectionHead, Btn, RowText, Ava, Tag } from './kit';
import { StatusPill } from './HostGuestList';

const SERIF = 'Times, "Times New Roman", serif';
const NAME = { fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', overflowWrap: 'anywhere' };
const META = { fontSize: 11, color: '#6B7280' };
const IDENTITY_BTN = { flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: 14, border: 'none', background: 'none', padding: 0, textAlign: 'left', color: 'inherit', cursor: 'pointer' };
const RULE = '2px solid #F2F2F2';

function applicantMeta(app) {
  const bits = [];
  if (app.guest?.handle) bits.push(`@${app.guest.handle}`);
  bits.push(`applied ${dateLabel(app.createdAt).toLowerCase()}`);
  const qty = app.quantity || 1;
  bits.push(`${qty} ${qty === 1 ? 'ticket' : 'tickets'}`);
  if (app.goldenTicketUsed) bits.push('golden ticket');
  return bits.join(' · ');
}

function ApplicantCard({ app, mobile, showActions, busy, onAccept, onDecline, onMessage, onView, first }) {
  const name = app.guest?.displayName || app.guestCheckoutName || 'Unknown';
  const vibe = app.guest?.vibeCheck;
  const hasMoney = committedCents(app) > 0;
  const canMessage = !!app.guest?.userId && !!onMessage;
  const canView = !!onView && !!app.guest?.userId;
  const actions = showActions && (
    <>
      <Btn variant="primary" size={mobile ? 'md' : 'sm'} full onClick={() => onAccept(app)} disabled={!!busy} data-testid={`accept-${app.id}`}>
        {busy === 'accept' ? 'ACCEPTING…' : 'ACCEPT'}
      </Btn>
      <div style={mobile ? { display: 'flex', gap: 6 } : { display: 'contents' }}>
        {canMessage && (
          <Btn variant="secondary" size={mobile ? 'md' : 'sm'} full={!mobile} style={mobile ? { flex: 1 } : undefined} onClick={() => onMessage(app)} disabled={!!busy}>{mobile ? 'MESSAGE' : 'MESSAGE FIRST'}</Btn>
        )}
        <Btn variant="danger" size={mobile ? 'md' : 'sm'} full={!mobile} style={mobile ? { flex: 1 } : undefined} onClick={() => onDecline(app)} disabled={!!busy} data-testid={`decline-${app.id}`}>
          {busy === 'decline' ? 'DECLINING…' : hasMoney ? (mobile ? 'DECLINE' : 'DECLINE · REFUND') : 'DECLINE'}
        </Btn>
      </div>
    </>
  );

  const identity = (
    <button type="button" onClick={canView ? () => onView(app) : undefined} disabled={!canView} style={{ ...IDENTITY_BTN, cursor: canView ? 'pointer' : 'default' }}>
      <Ava name={name} imageUrl={app.guest?.avatarUrl} size={mobile ? 44 : 56} />
      <span style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 8, rowGap: 4 }}>
          <span style={{ ...NAME, fontSize: mobile ? 13 : 14 }}>{name}</span>
          <span style={{ ...META, fontSize: mobile ? 10 : 11 }}>{applicantMeta(app)}</span>
        </span>
        {vibe && <span style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: mobile ? 14 : 15, lineHeight: 1.45, marginTop: 7, color: '#374151' }}>{vibe}</span>}
        {app.message && <span style={{ ...META, marginTop: 8, lineHeight: 1.5, overflowWrap: 'anywhere' }}>{app.message}</span>}
      </span>
    </button>
  );

  return (
    <div
      style={{ display: 'flex', flexDirection: mobile ? 'column' : 'row', alignItems: mobile ? 'stretch' : 'flex-start', gap: mobile ? 12 : 16, padding: first ? '0 0 16px' : '16px 0', borderTop: first ? 'none' : RULE }}
      data-testid={`applicant-${app.id}`}
    >
      {identity}
      {showActions && <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 'none', width: mobile ? '100%' : 150 }}>{actions}</div>}
    </div>
  );
}

function AnsweredRow({ app, pill, action, onView }) {
  const name = app.guest?.displayName || app.guestCheckoutName || 'Unknown';
  const canView = !!onView && !!app.guest?.userId;
  return (
    <div className="hev-line">
      <button type="button" onClick={canView ? () => onView(app) : undefined} disabled={!canView} style={{ ...IDENTITY_BTN, alignItems: 'center', gap: 12, cursor: canView ? 'pointer' : 'default' }}>
        <Ava name={name} imageUrl={app.guest?.avatarUrl} size={36} />
        <RowText title={name} meta={applicantMeta(app)} plainMeta />
      </button>
      <StatusPill tone={pill.tone}>{pill.label}</StatusPill>
      {action}
    </div>
  );
}

function TrickRow({ trick, tone, action }) {
  return (
    <div className="hev-line" style={{ opacity: trick.status === 'declined' ? 0.6 : 1 }}>
      <Ava name={trick.proposedByDisplayName || '?'} imageUrl={trick.proposedByAvatarUrl} size={36} />
      <RowText title={trick.text} meta={trick.proposedByDisplayName} plainMeta />
      {action || <StatusPill tone={tone}>{trick.status === 'accepted' ? 'ACCEPTED' : 'DECLINED'}</StatusPill>}
    </div>
  );
}

export default function HostApplications({ event, buckets, mobile = false, showActions = true, actionLoading = {}, onAccept, onDecline, onReinvite, onMessage, onView, onReviewTrick }) {
  const { pending, accepted, declined, interested } = buckets;
  const oldest = pending.length ? pending.reduce((o, a) => (new Date(a.createdAt) < new Date(o.createdAt) ? a : o), pending[0]) : null;
  const tricks = event?.partyTricks || [];
  const pendingTricks = tricks.filter((t) => t.status === 'pending');
  const acceptedTricks = tricks.filter((t) => t.status === 'accepted');
  const declinedTricks = tricks.filter((t) => t.status === 'declined');
  const answered = accepted.length + declined.length;
  const emptyCopy = event?.mode === 'curated'
    ? 'New applications land here. Accept or decline each one.'
    : 'Open events auto-accept — there is nothing waiting on you.';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {pending.length === 0 ? (
        <Card rise center testId="waiting-panel">
          <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase' }}>NOTHING TO REVIEW.</span>
          <span style={{ fontSize: 12, lineHeight: 1.6, color: '#6B7280', maxWidth: 420 }}>{emptyCopy}</span>
        </Card>
      ) : (
        <Card rise testId="waiting-panel">
          <SectionHead title={`WAITING ON YOU · ${pending.length}`} tag={oldest ? `OLDEST ${ago(oldest.createdAt)}` : undefined} />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {pending.map((app, i) => (
              <ApplicantCard key={app.id} app={app} mobile={mobile} showActions={showActions} busy={actionLoading[app.id]} onAccept={onAccept} onDecline={onDecline} onMessage={onMessage} onView={onView} first={i === 0} />
            ))}
          </div>
        </Card>
      )}

      <Card rise gap="10" testId="answered-panel">
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase' }}>ALREADY ANSWERED · {answered}</span>
        <span style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.6 }}>
          {answered === 0
            ? 'Nobody answered yet.'
            : <>{accepted.length} accepted, {declined.length} declined.{declined.some((a) => a.partyCreditsRefunded) ? ' Declines were refunded the same day.' : ''}</>}
        </span>
        {answered > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', marginTop: 4 }}>
            {accepted.map((app) => (
              <AnsweredRow
                key={app.id}
                app={app}
                onView={onView}
                pill={{ label: 'ACCEPTED', tone: 'outline' }}
                action={showActions && onDecline ? (
                  <Btn variant="danger" size="xs" onClick={() => onDecline(app)} disabled={!!actionLoading[app.id]} data-testid={`remove-${app.id}`}>{actionLoading[app.id] === 'decline' ? 'REMOVING…' : 'REMOVE'}</Btn>
                ) : null}
              />
            ))}
            {declined.map((app) => (
              <AnsweredRow
                key={app.id}
                app={app}
                onView={onView}
                pill={{ label: 'DECLINED', tone: 'ghost' }}
                action={showActions && onReinvite ? (
                  <Btn variant="secondary" size="xs" onClick={() => onReinvite(app)} disabled={!!actionLoading[app.id]} data-testid={`reinvite-${app.id}`}>{actionLoading[app.id] === 'reinvite' ? 'SENDING…' : 'REINVITE'}</Btn>
                ) : null}
              />
            ))}
          </div>
        )}
      </Card>

      {interested.length > 0 && (
        <Card rise gap="10" testId="interested-panel">
          <SectionHead title={`INTERESTED · ${interested.length}`} sub="These guests clicked through to buy tickets on the external site." tag="OFF PLATFORM" />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {interested.map((app) => (
              <AnsweredRow key={app.id} app={app} onView={onView} pill={{ label: 'INTERESTED', tone: 'hairline' }} />
            ))}
          </div>
        </Card>
      )}

      {tricks.length > 0 && (
        <Card rise gap="10" testId="tricks-panel">
          <SectionHead title={`PARTY TRICKS · ${acceptedTricks.length}/${tricks.length} ACCEPTED`} right={pendingTricks.length ? <Tag>{pendingTricks.length} AWAITING REVIEW</Tag> : undefined} />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {pendingTricks.map((t) => (
              <TrickRow
                key={t.id}
                trick={t}
                action={(
                  <span style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    <Btn variant="primary" size="xs" onClick={() => onReviewTrick(t, 'accepted')}>ACCEPT</Btn>
                    <Btn variant="danger" size="xs" onClick={() => onReviewTrick(t, 'declined')}>DECLINE</Btn>
                  </span>
                )}
              />
            ))}
            {acceptedTricks.map((t) => <TrickRow key={t.id} trick={t} tone="green" />)}
            {declinedTricks.map((t) => <TrickRow key={t.id} trick={t} tone="ghost" />)}
          </div>
        </Card>
      )}
      {!showActions && (pending.length > 0 || answered > 0) && (
        <span className="hev-caps-muted">
          {event?.status === 'draft' ? 'PUBLISH THE EVENT TO ANSWER APPLICATIONS.' : 'THIS EVENT IS CLOSED — APPLICATIONS ARE READ-ONLY.'}
        </span>
      )}
    </div>
  );
}
